import { GreyBox } from '@dealstack/frontend/shared/components/common/GreyBox';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch';
import {
	INVESTOR_TEST_ELEMENT_ID,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { AddressInput } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/AddressInput';
import { PivBasicInformationInput as PivBasicInformation } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/PivBasicInformationInput';
import { RepresentativePersonalInformationInput } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/RepresentativePersonalInformationInput';
import { AuthorisedSignatorySection } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPIVInformation/AuthorisedSignatorySection';
import { ControllingPartiesSection } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPIVInformation/ControllingPartiesSection';
import { EMPTY_CALLBACK } from '@dealstack/shared/lib/utils';
import { Flex, Title } from '@mantine/core';
import { FormProvider } from 'react-hook-form';

import {
	INVESTMENT_LEAVE_PIV_INFO_FORM,
	useInvestmentLeavePIVInformationController,
} from './useInvestmentLeavePIVInformationController';

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
}

export const InvestmentLeavePIVInformation = ({ setIsContinueDisabled, setIsLoading }: Props) => {
	const tInvestor = useTranslationInvestor();

	const { personalInformationForm, isControllingPartyRequired, investorPersonId } =
		useInvestmentLeavePIVInformationController({
			setIsContinueDisabled,
			setIsLoading,
		});

	const isPiv = personalInformationForm.watch(INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV);
	const disableRepresentativeFields = personalInformationForm.watch(
		INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
	);

	return (
		<Flex direction="column" gap="2rem">
			<FormProvider {...personalInformationForm}>
				<GreyBox>
					<Title order={5} sx={{ marginBottom: '0.5rem' }}>
						{tInvestor('investment.piv.question')}
					</Title>

					<UIElementIdentifier
						identifier={
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_PERSONAL_INFORMATION_IS_PIV_CHECKBOX
						}
					>
						<AdvancedSwitch
							twoWay={true}
							selectedValue={personalInformationForm.watch(
								INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV
							)}
							leftText={tInvestor('investment.piv.personally')}
							rightText={tInvestor('investment.piv.company')}
							onChange={EMPTY_CALLBACK}
							disabled
						/>
					</UIElementIdentifier>
				</GreyBox>

				<Flex gap="5rem">
					<PivBasicInformation
						pivNameFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_NAME}
						pivRegNumberFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_REG_NUMBER}
						disabled={!isPiv}
					/>

					<UIElementIdentifier
						identifier={INVESTOR_TEST_ELEMENT_ID.INVESTOR_PIV_ADDRESS_INPUTS}
					>
						<AddressInput
							countryFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_COUNTRY}
							cityFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_CITY}
							postCodeFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_POST_CODE}
							streetFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_STREET}
							houseNumberFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_HOUSE_NUMBER}
							title={tInvestor('investment.piv.address')}
							disabled={!isPiv}
						/>
					</UIElementIdentifier>
				</Flex>

				{isControllingPartyRequired ? (
					<Flex direction="column" gap="2rem">
						{isPiv && <ControllingPartiesSection />}
						<AuthorisedSignatorySection
							investorPersonId={investorPersonId}
							disabled={!isPiv}
						/>
					</Flex>
				) : (
					<>
						<GreyBox>
							<Title order={5} sx={{ marginBottom: '0.5rem' }}>
								{tInvestor('investment.piv.representative_question.question')}
							</Title>

							<UIElementIdentifier
								identifier={
									INVESTOR_TEST_ELEMENT_ID.INVESTOR_PERSONAL_INFORMATION_IS_REPRESENTATIVE_SAME_AS_INVESTOR
								}
							>
								<AdvancedSwitch
									twoWay={true}
									selectedValue={personalInformationForm.watch(
										INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
									)}
									leftValue={true}
									rightValue={false}
									leftText={tInvestor(
										'investment.piv.representative_question.yes'
									)}
									rightText={tInvestor(
										'investment.piv.representative_question.no'
									)}
									disabledWithMiddleState={!isPiv}
									onChange={() => {
										personalInformationForm.setValue(
											INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR,
											!personalInformationForm.watch(
												INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
											),
											{ shouldDirty: true }
										);
									}}
								/>
							</UIElementIdentifier>
						</GreyBox>

						<Flex gap="5rem">
							<RepresentativePersonalInformationInput
								firstNameFieldName={
									INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME
								}
								lastNameFieldName={
									INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME
								}
								emailFieldName={INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL}
								disabled={disableRepresentativeFields || !isPiv}
							/>
						</Flex>
					</>
				)}
			</FormProvider>
		</Flex>
	);
};
